import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { GlobalVariables } from '../../../global-variables';
import { DashboardService } from './dashboard.service';
import { StorageKey, StorageService } from './common/storage.service';
import { isPartnerPortalUser, UserTypeEnum } from './common/constant.service';

export type HierarchyScope = 'self' | 'branch';

export interface HierarchyUser {
  _id: string;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  roleType?: string;
  parentId?: string | null;
  displayName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HierarchyScopeService {
  hierarchyList: HierarchyUser[] = [];
  selectedUserId = '';
  scope: HierarchyScope = 'branch';
  showSelector = false;
  showScopeSelector = false;
  initialized = false;

  private initPromise?: Promise<void>;
  private currentUser: any;
  private loadedUserId = '';

  constructor(
    private http: HttpClient,
    private dashboardService: DashboardService,
    private storageService: StorageService,
  ) {}

  init(options?: {partnerPortal?: boolean}): Promise<void> {
    const currentUser = JSON.parse(
      this.storageService.getValue(StorageKey.currentUser) || '{}',
    );
    const userId = String(currentUser?.id ?? currentUser?._id ?? '');

    if (this.initialized && this.loadedUserId === userId) {
      this.applyPartnerPortalOptions(options?.partnerPortal);
      return Promise.resolve();
    }

    if (this.initialized && this.loadedUserId !== userId) {
      this.reset();
    }

    if (!this.initPromise) {
      this.initPromise = this.load(options?.partnerPortal);
    }
    return this.initPromise;
  }

  reset(): void {
    this.hierarchyList = [];
    this.selectedUserId = '';
    this.scope = 'branch';
    this.showSelector = false;
    this.showScopeSelector = false;
    this.initialized = false;
    this.initPromise = undefined;
    this.loadedUserId = '';
    this.currentUser = undefined;
  }

  buildScopeParams(): { userId: string; scope: HierarchyScope } {
    return {
      userId: this.selectedUserId,
      scope: this.scope,
    };
  }

  /** Partner portal: omit userId unless hierarchy selector is visible. */
  buildPartnerPortalScopeParams(
    useHierarchySelector: boolean,
  ): { userId?: string; scope: HierarchyScope } {
    if (useHierarchySelector && this.selectedUserId) {
      return this.buildScopeParams();
    }
    return { scope: 'branch' };
  }

  mergeScopeParams<T extends Record<string, unknown>>(params: T): T & {
    userId: string;
    scope: HierarchyScope;
  } {
    return {
      ...params,
      ...this.buildScopeParams(),
    };
  }

  onSelectionChange(): void {
    // hook for parent components to refresh data
  }

  private async load(partnerPortal = false): Promise<void> {
    this.currentUser = JSON.parse(
      this.storageService.getValue(StorageKey.currentUser) || '{}',
    );
    const types: string[] = this.currentUser?.userType ?? [];
    const userId = String(this.currentUser?.id ?? this.currentUser?._id ?? '');

    const isAdmin = types.includes(UserTypeEnum.ADMIN);
    const isRetailerOnly =
      types.length === 1 && types.includes(UserTypeEnum.RETAILER);
    const isHierarchyRole = isPartnerPortalUser(types);
    const isOrganization = types.some(t =>
      [
        UserTypeEnum.ORGANIZATION,
        UserTypeEnum.ORGANIZATION_CATALOG,
        UserTypeEnum.BASE_ORGANIZATION,
        UserTypeEnum.ORGANIZATION_LITE,
      ].includes(t),
    );

    this.selectedUserId = userId;
    this.scope = 'branch';

    if (isRetailerOnly) {
      this.showSelector = false;
      this.showScopeSelector = false;
      this.loadedUserId = userId;
      this.initialized = true;
      return;
    }

    this.showSelector = isAdmin || isHierarchyRole || isOrganization;
    this.showScopeSelector = false;

    this.applyPartnerPortalOptions(partnerPortal);

    if (isAdmin) {
      const response: any = await firstValueFrom(
        this.dashboardService.organization_list(),
      );
      if (response?.success) {
        this.hierarchyList = (response.data ?? []).map((user: any) =>
          this.normalizeUser(user),
        );
        if (this.hierarchyList.length > 0) {
          this.selectedUserId = this.hierarchyList[0]._id;
        }
      }
    } else if (this.showSelector) {
      const hierarchyUrl = partnerPortal
        ? `${GlobalVariables.appURL}partner-portal/hierarchy-list`
        : `${GlobalVariables.appURL}users/hierarchy-list`;
      const response: any = await firstValueFrom(this.http.get(hierarchyUrl));
      if (response?.success) {
        this.hierarchyList = (response.data ?? []).map((user: any) =>
          this.normalizeUser(user),
        );
      }
      if (isOrganization) {
        this.selectedUserId = userId;
      } else if (this.hierarchyList.length > 0) {
        this.selectedUserId = userId || this.hierarchyList[0]._id;
      }
    }

    this.loadedUserId = userId;
    this.initialized = true;
  }

  private applyPartnerPortalOptions(partnerPortal?: boolean): void {
    if (!partnerPortal || !this.currentUser) return;
  }

  private normalizeUser(user: any): HierarchyUser {
    const id = String(user._id ?? user.id);
    const roleLabel = user.roleType ? `${user.roleType} · ` : '';
    return {
      ...user,
      _id: id,
      id,
      displayName:
        user.displayName ??
        `${roleLabel}${user.firstName ?? ''} ${user.lastName ?? ''} (${user.email ?? ''})`.trim(),
    };
  }
}
